import { Container, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import WhySimpwillList, { ListInfo } from '@components/common/advantages-list';
import BannerWithCards, {
  CardInfo,
} from '@components/common/banner-with-cards';
import Button from '@components/common/button';
import FAQ from '@components/common/faq';
import FeefoRating from '@components/common/feefo-rating';
import Figures, { Circle, Image } from '@components/common/figures';
import Plans, { PlanInfo } from '@components/common/plans';
import StarsGenerator from '@components/common/stars-generator';
import Testimonials from '@components/common/testimonials';
import defaultPrices from '@config/constants/prices';
import useTypedSelector from '@hooks/use-typed-selector';
import Feefo from '@public/svg/feefo.svg';
import Smile from '@public/svg/happy-emoji.svg';
import Timer from '@public/svg/timer.svg';
import Update from '@public/svg/update.svg';
import { getReviews, getRating } from '@store/landings/thunks';
import minusDiscountPercentage from '@utils/minus-discount-percentage';
import useStyles from '@views/create-will/styles';

const DISCOUNT_PERCENT = 10;

const advantages: CardInfo[] = [
  {
    icon: <Timer />,
    title: 'Takes 15 minutes',
    description:
      "Simplicity at it's best. Simpwill makes a normally tedious and difficult process easy. ",
  },
  {
    icon: <Smile />,
    title: 'Peace of mind',
    description: `Your will is reviewed to confirm that you are happy with it and we can have our STEP
      qualified experts check it over for you.`,
  },
  {
    icon: <Update />,
    title: 'Estate Update',
    description:
      'Update your will as and when you need to. We know life changes at any point, so should your will.',
  },
];

const faqQuestions = (
  singleWillPrice: number,
  jointWillPrice: number,
  unlimitedPrice: number,
) => [
  {
    title:
      'Can I make a legally binding will online with Simpwill and without a solicitor?',
    text: (
      <Typography variant="body2">
        The simple answer is yes! Just so long as your wishes are relatively
        simple, there&apos;s no need to employ the services of a solicitor to
        draw up your will. It&apos;s perfectly legal to create your own will and
        with Simpwill&apos;s intuitive technology it is now easier than ever!{' '}
        <br /> We have a team of STEP qualified professionals (Society of Trust
        and Estate Practitioners) on hand to ensure that your will is drafted
        correctly and reflects your final wishes. Not only is Simpwill&apos;s
        online estate planning service the most efficient but also the most
        cost-effective way to create an online will in the UK.
      </Typography>
    ),
  },
  {
    title: 'What are the legalities of making a will online?',
    text: (
      <>
        <Typography variant="body2">
          Making a will online with Simpwill creates a fully legal and compliant
          will as long as you the testator meet the standard legal requirements
          for a UK will:
        </Typography>
        <ul>
          <li>
            <Typography variant="body1">You are over the age of 18.</Typography>
          </li>
          <li>
            <Typography variant="body1">
              You make the online will voluntarily and not under any coercion.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              You are &apos;of sound mind&apos; when you write the will.
            </Typography>
            This means that the testator is of comprehensive mental capacity at
            the time of writing the will.
          </li>
          <li>
            <Typography variant="body1">
              The will must be visibly written i.e. in printed text that
              Simpwill generate for you.
            </Typography>
            In other words a verbal or spoken will technically does not exist.
          </li>
          <li>
            <Typography variant="body1">
              The will must be signed by 2 individuals.
            </Typography>
            Both of your chosen witnesses must be over the age of 18 and cannot
            stand to benefit from the will in question.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: 'What happens if I die without a will?',
    text: (
      <Typography variant="body2">
        Dying without a valid will is called intestacy or dying intestate. If
        you were to die with no will in place then your estate goes to your next
        of kin, however If you&apos;re not married and not in a civil
        partnership, your partner is not legally entitled to anything when you
        die. Additionally, any Inheritance Tax that your estate has to pay might
        be higher than it would be if you had made a will. Simpwill alleviates
        any worries you may have and can ensure your other half is protected
        even if you&apos;re not married, and that specific items, pets, chattels
        etc are given to the correct people.
      </Typography>
    ),
  },
  {
    title: 'What kind of will does Simpwill produce?',
    text: (
      <Typography variant="body2">
        Simpwill&apos;s online will service generates a simple (sometimes
        referred to as a basic) will. This essentially states what you would
        like to happen to your estate upon your death, it also names your
        executors (appointed to administer the estate of a deceased person), and
        nominates your chosen guardians for your children and pets.
        <br /> Further estate planning is recommended for very large and/or
        complicated estates, where by you may want to set up trusts, lay out
        wishes that are complex and explore methods to mitigate inheritance tax
        exposure. We have a team of STEP (The Society of Trust and Estate
        Practitioners) qualified estate planning professionals that are on hand
        to give you free detailed guidance on further estate and tax planning.
      </Typography>
    ),
  },
  {
    title: 'What can I leave to others in my online will?',
    text: (
      <>
        <Typography variant="body2">
          Simpwill&apos;s online will maker allows you to determine exactly
          where you would like to distribute your estate, this can include the
          following and more:
        </Typography>
        <ul>
          <li>Money</li>
          <li>Property or land</li>
          <li>Specific belongings</li>
          <li>Stocks and shares</li>
        </ul>
      </>
    ),
  },
  {
    title: 'Can you create a will for couples?',
    text: (
      <Typography variant="body2">
        Again, Simpwill makes it easy for couples and partners to create a will
        that reflects each other&apos;s individual wishes. <br />
        Most will writing firms or even solicitors will try to cut corners and
        produce what is known as a mirror will. Mirror wills can cause
        significant problems and are often marketed as &apos;two for the price
        of one”. It is widely known in the profession that there is no benefit
        nor reason why two people should have identical (mirror) wills. A mirror
        will gives no flexibility as to how such assets should be best
        maintained. Simpwill allows couples to create their own individual and
        more importantly separate wills, providing greater protection that
        mirror wills simply cannot offer. <br />
        The first will is made by the lead will writer and then an invite is
        sent to your partner to create their own individual will with separate
        wishes specific to each partner. Simpwill&apos;s offer for couples
        (Individual wills) costs just £{jointWillPrice} which is £
        {jointWillPrice - singleWillPrice}
        less than if you were to purchase them separately and will save hundreds
        of pounds than if you were to use a traditional solicitor.
      </Typography>
    ),
  },
  {
    title: "How can you choose Simpwill's couples will?",
    text: (
      <Typography variant="body2">
        Simpwill makes it easy to create your own legally binding will and even
        easier for your partner to complete their own will at a time that is
        convenient for them! We will send out an email to your partner with
        details about how to complete their will, with a complete guide. Once
        they have completed their will they will be able to download it without
        having to make a payment.
      </Typography>
    ),
  },
  {
    title: 'What is an executor?',
    text: (
      <Typography variant="body2">
        An executor is someone who is named in the will as responsible for
        dealing with the estate. An executor is accountable for ensuring that
        your wishes are carried out in accordance with your wishes and the
        estate is settled properly. You may wish to appoint Simpwill&apos;s
        professional executors to take away the onus from family members during
        a period which will be understandably difficult for the family. Not only
        this, dealing with a deceased person&apos;s estate has become much more
        complex over recent years and our professionals will handle the full
        process with impartiality, minimum fuss and maximum efficiency.
      </Typography>
    ),
  },
  {
    title: 'How often do I need to update my online will?',
    text: (
      <Typography variant="body2">
        A good rule of thumb is that you should review your estate plan every
        three to five years or when there&apos;s a big change in your life.
        Simpwill makes it easy to update your will if and when you have a big
        life event. These events could include marriage, divorce and childbirth.
        For the first year all updates to your Simpwill online will are free of
        charge! Yes, that&apos;s right, should you need to make changes to your
        will in the first 12 months you can make those changes for no cost. For
        £{unlimitedPrice} a year (after 12 months free) you can subscribe to our
        estate planning update service (Estate Update), which allows you to make
        as many changes as you like to your will at any time. Also included
        within the Estate Update subscription is a secure storage facility for
        your will, meaning we will also keep a physical copy of your will for
        added piece of mind.
      </Typography>
    ),
  },
  {
    title: 'What is a guardian?',
    text: (
      <Typography variant="body2">
        If you have any children under the age of 18, you will need to choose a
        guardian during the online will making process with Simpwill. A guardian
        takes parental responsibility for children in the event of both
        parents&apos; death. Think carefully who you would like to appoint as a
        guardian as it is a big responsibility, however with Simpwill&apos;s
        Estate Update you will be able to change your guardians with ease. You
        can also nominate a guardian for your beloved pet, the nominees will be
        responsible for looking after them for as long as they survive.
      </Typography>
    ),
  },
  {
    title: 'What is a beneficiary?',
    text: (
      <Typography variant="body2">
        Beneficiary is the term used to describe a person whom you have decided
        to leave a gift to in your will. Beneficiaries can also include
        charities, Simpwill have a number of partnered charities to choose from
        and you can also select your own favoured charity to benefit from your
        estate. It is worth noting that the witnesses who sign your will cannot
        be beneficiaries of your estate.
      </Typography>
    ),
  },
];

const estateUpdateFeatures = [
  'Unlimited updates',
  'Updates to the law',
  'Secure digital storage',
];

const CreateWillView: React.FC = () => {
  const classes = useStyles();
  const router = useRouter();

  const referrer = router.query.referral as string;

  const {
    items: reviews,
    overalRating,
    lastFetchedReviews,
    lastFetchedRating,
    isLoading,
  } = useTypedSelector((state) => state.landings);

  const { prices } = useTypedSelector((state) => state.documents);

  const dispatch = useDispatch();

  useEffect(() => {
    // Fetches if no reviews in state or reviews are older than two hours
    if (reviews.length) {
      if (
        lastFetchedReviews &&
        lastFetchedReviews > Date.now() - 1000 * 60 * 60 * 2
      ) {
        dispatch(getReviews());
      }
    } else {
      dispatch(getReviews());
    }

    // Fetches if no rating in state or rating is older than one two hours
    if (overalRating) {
      if (
        lastFetchedRating &&
        lastFetchedRating > Date.now() - 1000 * 60 * 60 * 2
      ) {
        dispatch(getRating());
      }
    } else {
      dispatch(getRating());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTop: Circle[] = [
    {
      background: 'blueGradient',
      classWrapper: classes.figureOnTopCircleOne,
    },
    {
      background: 'pinkMain',
      classWrapper: classes.figureOnTopCircleTwo,
    },
    {
      background: 'pink',
      classWrapper: classes.figureOnTopCircleThree,
    },
  ];

  const imagesOnTop: Image[] = [
    {
      src: '/png/person-with-checkboard.png',
      classWrapper: classes.figureOnTopImageOne,
    },
    {
      src: '/png/table.png',
      classWrapper: classes.figureOnTopImageTwo,
    },
  ];

  const estateUpdateFigures: Circle[] = [
    {
      background: 'pink',
      classWrapper: classes.estateUpdateCircleOne,
    },
    {
      background: 'pinkMain',
      classWrapper: classes.estateUpdateCircleTwo,
    },
    {
      background: 'blueMain',
      classWrapper: classes.estateUpdateCircleThree,
    },
  ];
  const estateUpdateImages: Image[] = [
    {
      src: '/png/rocket.png',
      classWrapper: classes.estateUpdateImageOne,
    },
  ];

  const needHelpFigures: Circle[] = [
    {
      background: 'pink',
      classWrapper: classes.needHelpCircleOne,
    },
    {
      background: 'blueGradient',
      classWrapper: classes.needHelpCircleTwo,
    },
  ];

  const needHelpImages: Image[] = [
    {
      src: '/png/person-hand-up-small.png',
      classWrapper: classes.needHelpImageOne,
    },
  ];

  const needHelpImagesDesktop: Image[] = [
    {
      src: '/png/person-hand-up-small.png',
      classWrapper: classes.needHelpImageOneDesktop,
    },
  ];

  const whySimpwill: ListInfo[] = [
    {
      circle: {
        background: 'pinkGradient',
        classWrapper: classes.listLaptopCircle,
      },
      color: 'pink',
      image: {
        src: '/png/why-simpwill/laptop.png',
        classWrapper: classes.listLaptopImage,
      },
      title: 'Ensure your estate goes to the people you want it to',
      description: `Simpwill's online process makes writing your will effortless. You can quickly and
        conveniently complete your will in less than 15 minutes from the comfort of your own home.`,
    },
    {
      circle: {
        background: 'blueGradient',
        classWrapper: classes.listGlassCircle,
      },
      color: 'blue',
      image: {
        src: '/png/why-simpwill/glass.png',
        classWrapper: classes.listGlassImage,
      },
      title: (
        <>
          Check your will and <br /> request approval
        </>
      ),
      description: `After completing the Simpwill process you will then be able to check a proof
      copy of your will prior to purchasing. There is also an option for it to be checked over
      by our estate planning experts.`,
    },
    {
      circle: {
        background: 'purpleGradient',
        classWrapper: classes.listNotebookCircle,
      },
      color: 'purple',
      image: {
        src: '/png/why-simpwill/notebook.png',
        classWrapper: classes.listNotebookImage,
      },
      title: 'Print and sign it',
      description: `After you are happy with your will you are then required to print it and sign it,
        ensuring that it has been witnessed by your chosen witnesses.`,
    },
    {
      circle: {
        background: 'yellowGradient',
        classWrapper: classes.listArrowsCircle,
      },
      color: 'yellow',
      image: {
        src: '/png/why-simpwill/arrows.png',
        classWrapper: classes.listArrowsImage,
      },
      title: 'Estate Update',
      description: `We know circumstances change and as such your will should to. Subscribe to Simpwill's
        Estate Update for unlimited updates to your will at any time!`,
    },
  ];

  const plans: PlanInfo[] = [
    {
      title: 'Simpwill online will',
      action: {
        text: 'Start for free',
        href: '/create-will/poll',
      },
      withBorder: true,
      features: [
        {
          text: 'Legally binding will',
          enabled: true,
        },
        {
          text: 'Every will verified',
          enabled: true,
        },
        {
          text: 'Helping hand from start to finish',
          enabled: true,
        },
        {
          text: 'Live chat with Simpwill experts',
          enabled: true,
        },
        {
          text: '15 minutes to complete',
          enabled: true,
        },
      ],
      prices: [
        {
          value: `£${prices?.singleWillPrice || defaultPrices.singleWillPrice}`,
          text: 'for a single will',
        },
        {
          value: `£${prices?.jointWillPrice || defaultPrices.jointWillPrice}`,
          text: 'for partner wills',
        },
      ],
    },
    {
      title: 'Average solicitor',
      features: [
        {
          text: 'Legally binding will',
          enabled: true,
        },
        {
          text: 'Every will verified',
          enabled: true,
        },
        {
          text: 'Helping hand from start to finish',
          enabled: false,
        },
        {
          text: 'Live chat with Simpwill experts',
          enabled: false,
        },
        {
          text: 'Unlimited updates',
          enabled: false,
        },
        {
          text: 'Online how to guides',
          enabled: false,
        },
        {
          text: 'Updates to the law',
          enabled: false,
        },
      ],
      prices: [
        {
          value: '£200 - £1,000',
          text: 'per will',
        },
      ],
    },
  ];

  return (
    <>
      <section className="overflow-hidden">
        <section>
          <Container maxWidth={false} className={classes.container}>
            <div className={classes.wishes}>
              <Typography
                component="h1"
                variant="h3"
                className={classes.wishesTitle}
              >
                {referrer ? (
                  <>
                    We&apos;re giving you a {DISCOUNT_PERCENT}% discount on your
                    will
                  </>
                ) : (
                  <>
                    The most cost effective <br /> and convenient way to make a
                    will
                  </>
                )}
                <span className={classes.blueDot}>.</span>
              </Typography>

              <Typography
                variant="body2"
                component="h3"
                className={classes.subtitle}
              >
                {referrer ? (
                  <>
                    {referrer[0]}
                    {referrer.slice(1, -5).toLowerCase()} created a will using
                    our simple online will writing service, and you can too. Get
                    your will for{' '}
                    <b>
                      £
                      {minusDiscountPercentage(
                        prices?.singleWillPrice ||
                          defaultPrices.singleWillPrice,
                        DISCOUNT_PERCENT,
                      )}
                    </b>{' '}
                    (normally{' '}
                    <b>
                      £
                      {prices?.singleWillPrice || defaultPrices.singleWillPrice}
                    </b>
                    ) when you use the code <b>{referrer}</b> at checkout.
                  </>
                ) : (
                  <>
                    One of the most cost effective online will writers and
                    hundreds of pounds cheaper than a solicitor. Create your
                    will today for just{' '}
                    <b>
                      £
                      {prices?.singleWillPrice || defaultPrices.singleWillPrice}
                    </b>{' '}
                    or one for your partner too for{' '}
                    <b>
                      £{prices?.jointWillPrice || defaultPrices.singleWillPrice}
                    </b>
                    .
                  </>
                )}
              </Typography>

              <Button
                id="make-my-will-button"
                styleType="containedBlue"
                text="Make my will"
                fullWidthOnMobile
                href="/create-will/poll"
              />
              <section className={classes.feefoSmall}>
                <Feefo className={classes.feefoLogo} />
                {overalRating && <StarsGenerator rating={overalRating} />}
              </section>
            </div>
            <Figures
              classWrapper={classes.topFigures}
              images={imagesOnTop}
              circles={onTop}
            />
          </Container>
        </section>

        <section>
          <div className={classes.reasonWrapper}>
            <Container maxWidth={false}>
              <BannerWithCards separated info={advantages} />
            </Container>
          </div>
        </section>

        <FeefoRating overalRating={overalRating} />
        <Testimonials reviews={reviews} isLoading={isLoading} />

        <section>
          <Container maxWidth={false}>
            <Typography
              variant="h3"
              className={classNames(classes.simpWillTitle, classes.textCenter)}
            >
              Why Choose Simpwill?
            </Typography>

            <WhySimpwillList info={whySimpwill} />
          </Container>
        </section>
        <section className={classes.howCompare}>
          <Container maxWidth={false}>
            <div className={classes.relative}>
              <Typography
                variant="h3"
                className={classNames(
                  classes.textCenter,
                  classes.howCompareTitle,
                )}
              >
                How does Simpwill compare?
              </Typography>
              <Typography
                variant="body2"
                className={classNames(classes.gray, classes.textCenter)}
              >
                Simple. Efficient. Affordable
              </Typography>
              <Plans info={plans} />
            </div>
          </Container>
        </section>
        <section>
          <Container maxWidth={false}>
            <div className={classes.estateUpdate}>
              <Typography variant="subtitle1">
                Estate Update for £
                {prices?.unlimitedUpdates || defaultPrices.unlimitedUpdates} a
                year <span className={classes.gray}>(optional)</span>
              </Typography>
              {estateUpdateFeatures.map((feature) => (
                <div
                  key={`estate-update-${feature}`}
                  className={classes.feature}
                >
                  <i className="icon-checki-in-circle" />
                  <Typography variant="body2">{feature}</Typography>
                </div>
              ))}
              <Typography className={classes.gray} variant="body2">
                Begin for free, pay only when you complete and sign
              </Typography>
              <Button
                id="start-now-button"
                className={classes.startNow}
                styleType="containedBlue"
                text="Start now"
                href="/create-will/poll"
              />
              <Figures
                circles={estateUpdateFigures}
                images={estateUpdateImages}
              />
            </div>
          </Container>
        </section>
      </section>

      <section className={classes.faqWrapper}>
        <Container maxWidth={false}>
          <div className={classes.faq}>
            <Typography variant="h3">
              Simpwill Frequently Asked Questions
            </Typography>
            <FAQ
              info={faqQuestions(
                prices?.singleWillPrice || defaultPrices.singleWillPrice,
                prices?.jointWillPrice || defaultPrices.jointWillPrice,
                prices?.unlimitedUpdates || defaultPrices.unlimitedUpdates,
              )}
            />
          </div>
        </Container>
      </section>

      <section className={classes.needHelpWrapper}>
        <Container maxWidth={false}>
          <div className={classes.needHelp}>
            <Typography variant="h3">Need help?</Typography>
            <Typography variant="body2">
              If we haven&apos;t managed to answer your questions in our FAQ
              section below then please contact one of our friendly team using
              the{' '}
              <button
                id="live-chat-button-create-will"
                className={classes.liveChat}
                type="button"
                onClick={() => window.tidioChatApi.open()}
              >
                live chat
              </button>{' '}
              function.
            </Typography>
          </div>
          <Figures
            classWrapper={classes.needHelpFigures}
            circles={needHelpFigures}
            images={needHelpImages}
          />
          <Figures
            classWrapper={classes.needHelpFiguresDesktop}
            circles={[]}
            images={needHelpImagesDesktop}
          />
        </Container>
      </section>

      <section className={classes.ensureEstateWrapper}>
        <Container maxWidth={false}>
          <div className={classes.ensureEstate}>
            <Typography variant="subtitle1">
              Ensure your estate goes to the people you want it to.
            </Typography>
            <Button
              id="start-for-free-button-ensure-section"
              fullWidthOnMobile
              className={classes.ensureEstateButton}
              styleType="containedBlue"
              text="Start for free"
              href="/create-will/poll"
            />
          </div>
        </Container>
      </section>
      <section className={classes.feefoFooterWrapper}>
        <Container maxWidth={false}>
          <div className={classes.feefoFooter}>
            <Feefo className={classes.feefoLogoFooter} />
            <Typography className={classes.feefoRatingLabel} variant="body2">
              Average Customer Rating:
            </Typography>
            {overalRating && <StarsGenerator rating={overalRating} />}
          </div>
        </Container>
      </section>
    </>
  );
};

export default CreateWillView;
